import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchAppIntegrity } from "../helpers/fetch-app-integrity";
import {
  AppIntegrityReport,
  IntegrityCheck,
  IntegrityCheckStatus,
  IntegrityOverall,
  IntegritySystem,
} from "../types/app-integrity";
import { BORDER, INK, MUTED, Pill, type Tone } from "./admin-ui";

const STATUS_TONE: Record<IntegrityCheckStatus, Tone> = {
  ok: "green",
  fail: "red",
  warn: "amber",
  unknown: "grey",
};

const OVERALL: Record<IntegrityOverall, { tone: Tone; label: string }> = {
  healthy: { tone: "green", label: "healthy" },
  issues: { tone: "red", label: "issues" },
  partial: { tone: "amber", label: "partial" },
};

const SYSTEM_ORDER: IntegritySystem[] = ["registry", "keycloak", "coolify"];

function CheckRow({
  check,
  language,
}: {
  check: IntegrityCheck;
  language: SupportedLanguage;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Box>
        <Typography sx={{ color: INK, fontWeight: 600, fontSize: "0.85rem" }}>
          {check.label}
        </Typography>
        {check.detail && (
          <Typography variant="caption" sx={{ color: MUTED }}>
            {check.detail}
          </Typography>
        )}
      </Box>
      <Pill
        tone={STATUS_TONE[check.status]}
        label={getLocalizedMessage(
          language,
          `appIntegrityDialog.status.${check.status}`
        )}
      />
    </Box>
  );
}

export function AppIntegrityDialog({
  appId,
  appName,
  open,
  onClose,
  language,
}: {
  appId: string;
  appName: string;
  open: boolean;
  onClose: () => void;
  language: SupportedLanguage;
}) {
  const [report, setReport] = useState<AppIntegrityReport | null>(null);
  const [failed, setFailed] = useState(false);
  // Latest-fetch id; a resolved response applies only if still latest (drops stale runs).
  const requestId = useRef(0);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    const result = await fetchAppIntegrity(appId);
    if (id !== requestId.current) return; // superseded — drop the stale result
    setReport(result);
    setFailed(result === null);
  }, [appId]);

  // Reset to the loading state, then refetch (used by the Re-run button).
  const rerun = useCallback(() => {
    setReport(null);
    setFailed(false);
    void run();
  }, [run]);

  useEffect(() => {
    if (open) void run();
    return () => {
      // Invalidate any in-flight run so it can't set state after unmount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestId.current++;
    };
  }, [open, run]);

  // Reset on close; bump the request id so an in-flight run can't set state afterwards.
  const handleClose = () => {
    requestId.current++;
    setReport(null);
    setFailed(false);
    onClose();
  };

  const loading = open && !report && !failed;
  const overall = report ? OVERALL[report.overall] : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography sx={{ fontWeight: 700, color: INK }}>
            {getLocalizedMessage(language, "appIntegrityDialog.title", {
              appName,
            })}
          </Typography>
          {overall && (
            <Pill
              tone={overall.tone}
              label={getLocalizedMessage(
                language,
                `appIntegrityDialog.${overall.label}`
              )}
              dot
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && failed && (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography sx={{ color: MUTED, mb: 2 }}>
              {getLocalizedMessage(
                language,
                "appIntegrityDialog.couldNotRunCheck"
              )}
            </Typography>
            <Button
              startIcon={<RefreshRoundedIcon />}
              onClick={rerun}
              sx={{ textTransform: "none" }}
            >
              {getLocalizedMessage(language, "appIntegrityDialog.reRun")}
            </Button>
          </Box>
        )}

        {!loading && report && (
          <>
            {SYSTEM_ORDER.map((system) => {
              const checks = report.checks.filter((c) => c.system === system);
              if (checks.length === 0) return null;
              return (
                <Box key={system} sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: MUTED,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {getLocalizedMessage(
                      language,
                      `appIntegrityDialog.system.${system}`
                    )}
                  </Typography>
                  {checks.map((check) => (
                    <CheckRow
                      key={check.id}
                      check={check}
                      language={language}
                    />
                  ))}
                </Box>
              );
            })}
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
              <Button
                startIcon={<RefreshRoundedIcon />}
                onClick={rerun}
                sx={{ textTransform: "none" }}
              >
                {getLocalizedMessage(language, "appIntegrityDialog.reRun")}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
